import React from 'react';
import { AlertTriangle, CheckCircle2, Info, Plus, XCircle } from 'lucide-react';
import { Button } from '../../../components/ui';
import { formatFieldName, formatRuleName, severityLabel } from './format';
import { ItemStatus, SuggestedComponent } from './types';
import { FindingRef } from './validationEntries';

/**
 * One finding card in the validation detail pane: severity tile, check title
 * + tag, the engine's message, then the one-click fixes the data actually
 * supports — add each suggested component (independently; a row with three
 * parts keeps its remaining buttons after the first add) or apply a
 * suggested value. Checks without a machine fix show the engine's
 * recommendation and can only be skipped: honest, no invented actions.
 */
export interface FixCheckCardProps {
  finding: FindingRef;
  status: ItemStatus;
  isBusy: (key: string) => boolean;
  isComponentInBom: (name: string) => boolean;
  errorOf: (key: string) => string | undefined;
  /** Other components this finding also affects (context under this entry). */
  alsoAffects: string[];
  onAddComponent: (compKey: string, comp: SuggestedComponent) => void;
  onApplyValue: (finding: FindingRef) => void;
  onSkip: (key: string) => void;
  /** View-only (end user outside their fill window): findings shown, fix actions hidden. */
  readOnly?: boolean;
}

const severityTile: Record<string, string> = {
  critical: 'border-danger-border bg-danger-bg text-danger-ink',
  warning: 'border-warn-border bg-warn-bg text-warn-ink',
  info: 'border-action-border bg-action-bg text-action-deep',
};

const severityTag: Record<string, string> = {
  critical: 'bg-danger-bg text-danger-ink',
  warning: 'bg-warn-bg text-warn-ink',
  info: 'bg-action-bg text-action-deep',
};

export const FixCheckCard: React.FC<FixCheckCardProps> = ({
  finding,
  status,
  isBusy,
  isComponentInBom,
  errorOf,
  alsoAffects,
  onAddComponent,
  onApplyValue,
  onSkip,
  // Effective flag threaded from the validation hook (viewer + handoff,
  // canFill wins) — the card must not consult the wizard-access context
  // itself, or the pending end user's fixes would lock.
  readOnly = false,
}) => {
  const { result, key } = finding;
  const componentsToAddKeys = (result.suggestedComponentsToAdd || []).map((_, i) => `${key}-add-${i}`);
  const error = [key, ...componentsToAddKeys].map(errorOf).find(Boolean);
  const severity = severityLabel(result);
  const resolved = status !== 'needs_review';
  const canApplyValue = !!result.suggestedValue && !!result.affectedField;
  const componentsToAdd = result.suggestedComponentsToAdd || [];
  const SeverityIcon = severity === 'info' ? Info : AlertTriangle;

  return (
    <div
      className={`rounded-[9px] border border-line bg-surface px-s5 py-[12px] ${
        resolved ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start gap-[11px]">
        <span
          className={`flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[7px] border ${
            status === 'applied' ? 'border-success-border bg-success-bg text-success-ink' : severityTile[severity]
          }`}
        >
          {status === 'applied' ? <CheckCircle2 className="h-3 w-3" /> : <SeverityIcon className="h-3 w-3" />}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-s3">
            <span className="text-row-title text-ink">{formatRuleName(result.ruleName)}</span>
            <span className={`rounded-[4px] px-[7px] py-[1px] text-[10.5px] font-semibold ${severityTag[severity]}`}>
              {severity}
            </span>
            {status === 'applied' && (
              <span className="rounded-[4px] bg-success-bg px-[7px] py-[1px] text-[10.5px] font-semibold text-success-ink">
                fixed
              </span>
            )}
            {status === 'skipped' && (
              <span className="rounded-[4px] bg-neutral-bg px-[7px] py-[1px] text-[10.5px] font-semibold text-neutral-ink">
                skipped
              </span>
            )}
          </div>

          <p className="m-0 mt-[3px] text-body-sm text-ink-secondary">
            {result.message}
            {alsoAffects.length > 0 && (
              <>
                {' '}Also affects{' '}
                {alsoAffects.map((c, i) => (
                  <React.Fragment key={c}>
                    {i > 0 && ', '}
                    <span className="font-mono text-mono-data text-ink-body">{c}</span>
                  </React.Fragment>
                ))}
                .
              </>
            )}
          </p>

          {!resolved && result.recommendation && !canApplyValue && componentsToAdd.length === 0 && (
            <p className="m-0 mt-[6px] text-body-sm text-ink-body">{result.recommendation}</p>
          )}

          {!resolved && !readOnly && (
            <div className="mt-s3 flex flex-wrap items-center gap-s4">
              {componentsToAdd.map((comp, i) => {
                const compKey = `${key}-add-${i}`;
                const busy = isBusy(compKey);
                const added = isComponentInBom(comp.name);
                return (
                  <Button
                    key={compKey}
                    size="sm"
                    disabled={added}
                    loading={busy}
                    loadingLabel="Adding…"
                    iconLeft={added ? <CheckCircle2 /> : <Plus />}
                    onClick={() => onAddComponent(compKey, comp)}
                  >
                    {added ? `Added ${comp.name}` : `Add ${comp.name}`}
                  </Button>
                );
              })}
              {canApplyValue && (
                <Button
                  size="sm"
                  loading={isBusy(key)}
                  loadingLabel="Applying…"
                  onClick={() => onApplyValue(finding)}
                >
                  {`Set ${formatFieldName(result.affectedField!)} to ${result.suggestedValue}`}
                </Button>
              )}
              <button
                type="button"
                onClick={() => onSkip(key)}
                className="text-meta font-semibold text-neutral-ink hover:text-ink"
              >
                Skip
              </button>
            </div>
          )}

          {error && (
            <p className="m-0 mt-s2 flex items-center gap-s2 text-meta font-medium text-danger-ink">
              <XCircle className="h-[11px] w-[11px] shrink-0" />
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FixCheckCard;
